import {
  Avatar,
  Badge,
  Box,
  Group,
  Paper,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { IconClock, IconMail, IconMapPin, IconPhone, IconUsers } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ParticipantAttendanceFhirStatus } from '@/pages/appointments/utils/participantAttendance';
import {
  DEFAULT_PARTICIPANT_ATTENDANCE_STATUS,
  PARTICIPANT_ATTENDANCE_STATUS_OPTIONS,
} from '@/pages/appointments/utils/participantAttendance';
import { usePermissions } from '@/contexts/PermissionContext';
import { useHasPermission } from '@/hooks/useRbac';
import type { SessionAppointment } from '../data/mockSessionData';
import type { SessionAttendanceEntry } from '../lib/sessionAttendance';
import type { SessionContactInfo } from '../lib/sessionContactInfo';
import { zoomSidebarComboboxProps } from '../lib/zoomSidebarComboboxProps';
import type { ClientLocationConfirmation } from '@/lib/sessionLocation/types';
import type { ClientLocationConfirmationFormInput } from '@/schemas/client-location-confirmation.schema';
import { useSessionAutosave } from '../hooks/useSessionAutosave';
import { SessionAutosaveStatusText } from './SessionAutosaveStatusText';
import { ParticipantSessionLocationSection } from './ParticipantSessionLocationSection';

function IconValueRow({
  icon,
  value,
}: {
  icon: ReactNode;
  value: string | undefined;
}): JSX.Element {
  return (
    <Group gap={8} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
      <Box c="dimmed" style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        {icon}
      </Box>
      <Text size="xs" c="gray.7" truncate style={{ minWidth: 0, flex: 1 }}>
        {value?.trim() || '—'}
      </Text>
    </Group>
  );
}

interface ParticipantRow extends SessionContactInfo {
  status: ParticipantAttendanceFhirStatus;
}

function buildParticipantRows(
  participants: SessionContactInfo[],
  appointment: SessionAppointment,
  initialAttendance: Map<string, ParticipantAttendanceFhirStatus>,
): ParticipantRow[] {
  const byId = new Map<string, SessionContactInfo>();
  for (const p of participants) {
    if (p.patientId) byId.set(p.patientId, p);
  }

  const rosterSource =
    appointment.groupRoster && appointment.groupRoster.length > 0
      ? appointment.groupRoster.map((p) => ({
        patientId: p.id,
        name: p.name,
        initials: p.initials,
      }))
      : [
        {
          patientId: appointment.client.id,
          name: appointment.client.name,
          initials: appointment.client.initials,
        },
      ];

  return rosterSource.map((roster) => {
    const contact = byId.get(roster.patientId);
    return {
      patientId: roster.patientId,
      name: contact?.name ?? roster.name,
      initials: contact?.initials ?? roster.initials,
      phones: contact?.phones ?? [],
      emails: contact?.emails ?? [],
      address: contact?.address,
      isPrimary: contact?.isPrimary,
      status: initialAttendance.get(roster.patientId) ?? DEFAULT_PARTICIPANT_ATTENDANCE_STATUS,
    };
  });
}

function attendanceEntriesEqual(a: SessionAttendanceEntry[], b: SessionAttendanceEntry[]): boolean {
  if (a.length !== b.length) return false;
  const byId = new Map(b.map((e) => [e.patientId, e.status]));
  return a.every((e) => byId.get(e.patientId) === e.status);
}

/** Mockup label: "Scheduled: 15:00" (24h clock, start time only). */
function formatScheduledLabel(appointment: SessionAppointment): string | null {
  const start = new Date(appointment.scheduledTime);
  if (Number.isNaN(start.getTime())) return null;
  const time = start.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `Scheduled: ${time}`;
}

export function SessionParticipantsSection({
  participants,
  appointment,
  initialAttendance,
  onSaveAttendance,
  onRegisterFlush,
  clientLocationConfirmations = {},
  clientLocationLoading = false,
  clientLocationSaving = false,
  clientLocationSaveError = null,
  onSaveClientLocation,
}: {
  participants: SessionContactInfo[];
  appointment: SessionAppointment;
  initialAttendance: Map<string, ParticipantAttendanceFhirStatus>;
  onSaveAttendance: (entries: SessionAttendanceEntry[]) => Promise<void>;
  onRegisterFlush?: (flush: (() => Promise<void>) | null) => void;
  clientLocationConfirmations?: Record<string, ClientLocationConfirmation>;
  clientLocationLoading?: boolean;
  clientLocationSaving?: boolean;
  clientLocationSaveError?: string | null;
  onSaveClientLocation?: (
    patientId: string,
    values: ClientLocationConfirmationFormInput,
  ) => Promise<void>;
}): JSX.Element {
  const { ready: permissionsReady } = usePermissions();
  const canEditAttendance = useHasPermission('can_edit_attendance');
  const canEdit = permissionsReady && canEditAttendance;

  const statusOptions = useMemo(
    () =>
      PARTICIPANT_ATTENDANCE_STATUS_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
      })),
    [],
  );

  const attendanceSyncKey = useMemo(
    () =>
      JSON.stringify(
        [...initialAttendance.entries()].sort(([a], [b]) => a.localeCompare(b)),
      ),
    [initialAttendance],
  );

  const participantsSyncKey = useMemo(
    () => JSON.stringify(participants),
    [participants],
  );

  const [rows, setRows] = useState<ParticipantRow[]>(() =>
    buildParticipantRows(participants, appointment, initialAttendance),
  );
  const [baselineEpoch, setBaselineEpoch] = useState(0);

  useEffect(() => {
    setRows(buildParticipantRows(participants, appointment, initialAttendance));
    setBaselineEpoch((n) => n + 1);
    // Intentionally keyed by sync fingerprints to avoid resetting while local edits are in flight.
  }, [appointment.id, attendanceSyncKey, participantsSyncKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const entries = useMemo(
    (): SessionAttendanceEntry[] =>
      rows.map((p) => ({
        patientId: p.patientId,
        status: p.status,
      })),
    [rows],
  );

  const persistAttendance = useCallback(
    async (next: SessionAttendanceEntry[]) => {
      await onSaveAttendance(next);
    },
    [onSaveAttendance],
  );

  const autosave = useSessionAutosave({
    value: entries,
    persist: persistAttendance,
    debounceMs: 350,
    enabled: canEdit && rows.length > 0,
    contextKey: `${appointment.id}:${baselineEpoch}`,
    isEqual: attendanceEntriesEqual,
  });

  useEffect(() => {
    onRegisterFlush?.(autosave.flush);
    return () => onRegisterFlush?.(null);
  }, [autosave.flush, onRegisterFlush]);

  const updateStatus = (patientId: string, status: ParticipantAttendanceFhirStatus) => {
    if (!canEdit) return;
    setRows((prev) => prev.map((p) => (p.patientId === patientId ? { ...p, status } : p)));
  };

  const presentCount = rows.filter((p) => p.status === 'attended').length;
  const isGroup = rows.length > 1;
  const scheduledLabel = formatScheduledLabel(appointment);

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Group gap="xs">
          <IconUsers size={16} color="var(--mantine-color-dimmed)" />
          <Text size="sm" fw={600}>
            Participants
          </Text>
        </Group>
        {isGroup && (
          <Badge variant="light" color="green" size="sm">
            {presentCount}/{rows.length} attended
          </Badge>
        )}
      </Group>

      {rows.length === 0 ? (
        <Text size="sm" c="dimmed">
          No participants for this session.
        </Text>
      ) : (
        <Stack gap="sm">
          {rows.map((participant) => (
            <Paper
              key={participant.patientId}
              withBorder
              p="sm"
              radius="sm"
              style={{ borderColor: 'var(--mantine-color-gray-3)' }}
            >
              <Stack gap="sm">
                <Group gap="xs" wrap="nowrap" align="flex-start" justify="space-between">
                  <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }} align="center">
                    <Avatar size={28} radius="xl" color="gold" variant="light" style={{ flexShrink: 0 }}>
                      <Text fz={10} fw={600}>
                        {participant.initials}
                      </Text>
                    </Avatar>
                    <Text size="sm" fw={600} truncate style={{ minWidth: 0 }}>
                      {participant.name}
                    </Text>
                    {participant.isPrimary ? (
                      <Badge size="xs" variant="light" color="gold" tt="uppercase" style={{ flexShrink: 0 }}>
                        Primary
                      </Badge>
                    ) : null}
                  </Group>
                  {scheduledLabel ? (
                    <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }} c="dimmed">
                      <IconClock size={12} />
                      <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                        {scheduledLabel}
                      </Text>
                    </Group>
                  ) : null}
                </Group>

                <Stack gap={6}>
                  <IconValueRow
                    icon={<IconPhone size={14} />}
                    value={participant.phones.join(', ') || undefined}
                  />
                  <IconValueRow
                    icon={<IconMail size={14} />}
                    value={participant.emails.join(', ') || undefined}
                  />
                  <IconValueRow
                    icon={<IconMapPin size={14} />}
                    value={participant.address}
                  />
                </Stack>

                <Box
                  onPointerDownCapture={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Select
                    label="Attendance"
                    size="sm"
                    w="100%"
                    allowDeselect={false}
                    comboboxProps={zoomSidebarComboboxProps()}
                    styles={{
                      label: {
                        marginBottom: 6,
                        fontSize: 'var(--mantine-font-size-xs)',
                        fontWeight: 500,
                        color: 'var(--mantine-color-gray-7)',
                      },
                      input: {
                        borderColor: 'var(--mantine-color-gray-3)',
                      },
                    }}
                    placeholder={
                      !permissionsReady
                        ? 'Loading permissions…'
                        : canEdit
                          ? 'Select attendance'
                          : 'Attendance editing not permitted'
                    }
                    data={statusOptions}
                    value={participant.status}
                    onChange={(v) => {
                      if (!v) return;
                      updateStatus(participant.patientId, v as ParticipantAttendanceFhirStatus);
                    }}
                    onBlur={() => {
                      void autosave.flush();
                    }}
                    disabled={!canEdit}
                  />
                </Box>

                {onSaveClientLocation ? (
                  <ParticipantSessionLocationSection
                    patientId={participant.patientId}
                    confirmation={clientLocationConfirmations[participant.patientId] ?? null}
                    appliesToThisParticipant
                    loading={clientLocationLoading && Boolean(participant.isPrimary)}
                    saving={clientLocationSaving}
                    saveError={clientLocationSaveError}
                    onSave={onSaveClientLocation}
                  />
                ) : null}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <SessionAutosaveStatusText
        status={autosave.status}
        error={autosave.error}
        onRetry={() => {
          void autosave.retry();
        }}
      />
    </Stack>
  );
}
